import { ShieldCheck } from 'lucide-react';
import { lazy } from 'react';
import { msg } from '@/lib/i18n';
import { registerInvalidation } from '@/lib/realtime';
import { registerEntityHref } from '@/ui';
import { registerEntityTab } from '../entityTabs';
import { defineModule } from '../types';
import { tachoKeys } from './api';
import { cardsRoute, complianceRoute, downloadsRoute, fileRoute, filesRoute, hoursRoute, infringementsRoute } from './routes';

/** Compliance: EU 561 driving hours, infringements, tachograph files, downloads and company cards. */
export const tachoModule = defineModule({
  key: 'tacho',
  routes: [complianceRoute, hoursRoute, infringementsRoute, filesRoute, fileRoute, downloadsRoute, cardsRoute],
  nav: [
    {
      key: 'compliance',
      label: msg('Compliance'),
      description: msg('Are we compliant?'),
      icon: ShieldCheck,
      to: '/compliance',
      section: 'improve',
      order: 60,
      module: 'tacho',
      permission: ['tacho.activity.read', 'tacho.infringement.read', 'tacho.file.read', 'tacho.download.read', 'tacho.card.read'],
      match: ['/compliance'],
      children: [
        { key: 'hours', label: msg('Driving hours'), to: '/compliance/hours', permission: 'tacho.activity.read' },
        { key: 'infringements', label: msg('Infringements'), to: '/compliance/infringements', permission: 'tacho.infringement.read' },
        { key: 'files', label: msg('Tachograph files'), to: '/compliance/files', permission: 'tacho.file.read' },
        { key: 'downloads', label: msg('Downloads'), to: '/compliance/downloads', permission: 'tacho.download.read' },
        { key: 'cards', label: msg('Company cards'), to: '/compliance/cards', permission: 'tacho.card.read' },
      ],
    },
  ],
  setup() {
    registerEntityHref('tacho_file', (id) => `/compliance/files/${id}`);
    registerEntityHref('tacho_infringement', (id) => `/compliance/infringements?selected=${id}`);
    registerEntityTab('driver', {
      key: 'tachograph',
      label: msg('Driving hours'),
      order: 45,
      module: 'tacho',
      permission: 'tacho.activity.read',
      component: lazy(() => import('./tabs/DriverHoursTab')),
    });
    registerEntityTab('vehicle', {
      key: 'tachograph',
      label: msg('Tachograph'),
      order: 55,
      module: 'tacho',
      permission: ['tacho.file.read', 'tacho.download.read'],
      component: lazy(() => import('./tabs/VehicleTachoTab')),
    });
    // Tacho events are about a driver, a vehicle or a company card: refresh the compliance views.
    const tacho = (n: { type: string }) => (n.type.startsWith('tacho.') ? [tachoKeys.all] : []);
    registerInvalidation('driver', tacho);
    registerInvalidation('vehicle', tacho);
    registerInvalidation('company_card', () => [tachoKeys.cards, ['tacho', 'overview']]);
  },
});
